import { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  TrendingUp,
  Upload,
  Clock,
  Truck,
  Sparkles,
  Info,
} from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels, dashboardLabels } from "@/i18n";
import VideoPlayer from "@/components/support/VideoPlayer";
import { HOW_IT_WORKS_HERO_VIDEO_URL } from "@/config/constants";
import { Play } from "lucide-react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

interface QuickGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showQuickGuide: boolean; // Whether this is the first time (showQuickGuide = true)
}

const VehicleTestInfoDialog = ({
  children,
  labels,
}: {
  children: React.ReactNode;
  labels: Record<string, string>;
}) => {
  const { isNative } = useNativePlatform();
  const items = [
    labels.vehicleTestSpeed,
    labels.vehicleTestVibrations,
    labels.vehicleTestLeaks,
    labels.vehicleTestNoises,
    labels.vehicleTestGearbox,
    labels.vehicleTestCrash,
    labels.vehicleTestAc,
    labels.vehicleTestDashboard,
    labels.vehicleTestWindshield,
    labels.vehicleTestBrakes,
    labels.vehicleTestTires,
  ];
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent nested={isNative} compact className="max-w-md max-h-[80vh] overflow-y-auto">
        <div className="text-sm space-y-3">
          <h3 className="font-semibold text-lg text-blue-500">
            {labels.vehicleTestTitle}
          </h3>
          <p>
            <b>{labels.vehicleTest}</b> {labels.vehicleTestIntro}
          </p>
          <p>{labels.vehicleTestSections}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            {items.filter(Boolean).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <p className="text-accent font-bold">{labels.vehicleTestCta}</p>
          <p className="text-blue-500">{labels.vehicleTestTip}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const buildGuideSteps = (labels: Record<string, string>) => [
  {
    id: 1,
    title: labels.step1Title || "Fordonsvärdering",
    icon: <TrendingUp className="w-12 h-12 text-purple-500" />,
    content: labels.step1Content || "",
    gradient: "from-purple-900/20 via-pink-900/10 to-transparent",
  },
  {
    id: 2,
    title: labels.step2Title || "Lägg upp annons/Fordonstest",
    icon: <Upload className="w-12 h-12 text-orange-500" />,
    content: (
      <>
        {labels.step2Intro}
        <VehicleTestInfoDialog labels={labels}>
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border-2 border-primary/30 bg-primary/10 hover:bg-accent/50 text-blue-400 text-xs font-semibold focus:outline-none underline"
            aria-label={labels.vehicleTestAria || "Vad är Fordonstest?"}
          >
            {labels.vehicleTest}
            <Info className="w-3 h-3" />
          </button>
        </VehicleTestInfoDialog>
        {labels.step2Outro}
      </>
    ),
    gradient: "from-orange-900/20 via-yellow-900/10 to-transparent",
  },
  {
    id: 3,
    title: labels.step3Title || "Efter godkänd granskning",
    icon: <Clock className="w-12 h-12 text-blue-500" />,
    content: labels.step3Content || "",
    gradient: "from-blue-900/20 via-cyan-900/10 to-transparent",
  },
  {
    id: 4,
    title: labels.step4Title || "Vid en lyckad försäljning",
    icon: <Truck className="w-12 h-12 text-emerald-500" />,
    content: labels.step4Content || "",
    gradient: "from-emerald-900/20 via-green-900/10 to-transparent",
  },
  {
    id: 5,
    title: labels.step5Title || "Super! Nu är du expert på BEA systemet",
    icon: <CheckCircle2 className="w-12 h-12 text-green-500" />,
    content: (
      <div className="w-full space-y-3 text-center">
        <p>{labels.step5Ready}</p>
        <div className="text-left space-y-2 mt-3 p-3 rounded-xl bg-white/20 dark:bg-black/20 border border-white/30 text-sm">
          <p className="font-semibold text-foreground/90">
            {labels.step5SideButton}
          </p>
          <p>{labels.step5ViaButton}</p>
          <ul className="list-disc list-inside space-y-0.5 ml-2 text-xs">
            <li>
              <strong>{labels.step5OpenGuide}</strong>
            </li>
            <li>
              <strong>{labels.step5ReportBug}</strong>
            </li>
            <li>
              <strong>{labels.step5GiveFeedback}</strong>
            </li>
          </ul>
        </div>
      </div>
    ),
    gradient: "from-green-900/20 via-emerald-900/10 to-transparent",
    isFinal: true,
  },
];

export const QuickGuide = ({
  open,
  onOpenChange,
  showQuickGuide,
}: QuickGuideProps) => {
  const { isNative } = useNativePlatform();
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showHowItWorksVideo, setShowHowItWorksVideo] = useState(false);
  const { updateShowQuickGuideMutation } = useUser();
  const { language } = useLanguage();
  const labels = dashboardLabels(language)?.quickGuide || {};
  const common = commonLabels(language);
  const howVideoBtnTitle =
    common.menuPages?.howItWorks?.hero?.videoButton?.title ??
    (language === "en" ? "See how it works" : "Se hur det fungerar");
  const howVideoBtnSubtitle =
    common.menuPages?.howItWorks?.hero?.videoButton?.subtitle ??
    (language === "en" ? "Watch the intro video" : "Spela introduktionsvideo");
  const howVideoModalTitle =
    common.menuPages?.howItWorks?.hero?.videoModalTitle ??
    (language === "en" ? "How BILENIA works" : "Hur BILENIA fungerar");
  const guideSteps = buildGuideSteps(labels);

  // Reset to first step when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setShowConfirmDialog(false);
      setDontShowAgain(false);
      setShowHowItWorksVideo(false);
    }
  }, [open]);

  // Lock body scroll when Quick Guide is open so only the dialog scrolls (avoids 2 scrollbars)
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Native: block touches on guide sheet while video overlay is open
  useEffect(() => {
    if (!isNative) return;
    const cls = "bilenia-native-video-open";
    if (showHowItWorksVideo) {
      document.documentElement.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
    }
    return () => document.documentElement.classList.remove(cls);
  }, [showHowItWorksVideo, isNative]);

  const handleClose = (open: boolean) => {
    if (!open) {
      // Dialog is being closed
      if (showQuickGuide) {
        // First time - show confirmation instead of closing
        setShowConfirmDialog(true);
      } else {
        // Not first time - close directly
        onOpenChange(false);
      }
    }
  };

  const handleConfirmClose = async () => {
    if (dontShowAgain) {
      // Update backend to not show again
      try {
        await updateShowQuickGuideMutation.mutateAsync(false);
        toast.success(
          labels.guideNoLongerShown ||
            "Guiden visas inte längre automatiskt vid inloggning",
        );
      } catch (error) {
        toast.error(labels.couldNotUpdateSetting || "Could not update setting");
      }
    }
    setShowConfirmDialog(false);
    onOpenChange(false);
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const handleNext = () => {
    if (currentStep < guideSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinalClose = () => {
    if (showQuickGuide) {
      // First time - show confirmation even from final step
      setShowConfirmDialog(true);
    } else {
      onOpenChange(false);
    }
  };

  const currentGuideStep = guideSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === guideSteps.length - 1;

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          expandable={isNative}
          sheetOpen={open}
          className={cn(
            "max-w-2xl max-h-[85dvh] overflow-hidden p-0 sm:p-0 [&>button]:hidden rounded-xl sm:rounded-2xl",
            isNative && "native-sheet-expandable max-w-none w-full",
          )}
        >
          <div className="relative flex flex-col h-full max-h-[85dvh]">
            {/* Close button - always visible */}
            <button
              onClick={() => handleClose(false)}
              className="absolute right-3 top-3 z-10 rounded-full opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none bg-background/90 backdrop-blur-sm p-2 border border-border shrink-0 h-9 w-9 flex items-center justify-center"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{labels.close || "Close"}</span>
            </button>

            {/* Content - scrollable so X stays reachable on mobile; no right padding so X overlays and content keeps full width */}
            <div className="p-6 md:p-8 overflow-y-auto flex-1 min-h-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Step indicator */}
                  <div className="flex items-center justify-center gap-2 mb-6">
                    {guideSteps.map((_, index) => (
                      <div
                        key={index}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index <= currentStep
                            ? "bg-primary w-8"
                            : "bg-gray-300 w-2"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Card */}
                  <div
                    className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${currentGuideStep.gradient} border border-white/20 shadow-xl`}
                  >
                    <div className="p-6 md:p-8">
                      {/* Icon and Title */}
                      <div className="flex flex-col items-center text-center mb-6">
                        <motion.div
                          className="p-4 rounded-full bg-white/20 backdrop-blur-sm mb-4"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.3 }}
                        >
                          {currentGuideStep.icon}
                        </motion.div>
                        <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-transparent bg-clip-text mb-2">
                          {currentGuideStep.title}
                        </h2>
                      </div>

                      {/* Content */}
                      <div className="text-center text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed">
                        {typeof currentGuideStep.content === "string" ? (
                          <p>{currentGuideStep.content}</p>
                        ) : (
                          <div className="flex flex-wrap items-center justify-center gap-1">
                            {currentGuideStep.content}
                          </div>
                        )}
                      </div>

                      {/* Final step special content */}
                      {currentGuideStep.isFinal && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.2 }}
                          className="mt-6 flex flex-col items-center gap-4"
                        >
                          <div className="p-4 rounded-full bg-gradient-to-br from-green-400/20 to-emerald-400/20">
                            <Sparkles className="w-8 h-8 text-green-500" />
                          </div>

                          <Button
                            type="button"
                            variant="secondary"
                            className="gap-2"
                            onClick={() => setShowHowItWorksVideo(true)}
                            aria-label={`${howVideoBtnTitle} (video). ${howVideoBtnSubtitle}`}
                          >
                            <Play className="h-4 w-4 shrink-0" />
                            <span className="flex flex-col items-start leading-tight text-left">
                              <span className="font-semibold">
                                {howVideoBtnTitle} (video)
                              </span>
                              <span className="text-xs opacity-80">
                                {howVideoBtnSubtitle}
                              </span>
                            </span>
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex items-center justify-between gap-4 pt-4">
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      disabled={isFirstStep}
                      className="flex items-center gap-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {labels.previous || "Föregående"}
                    </Button>

                    <div className="text-sm text-gray-500">
                      {currentStep + 1} {labels.of || "av"} {guideSteps.length}
                    </div>

                    {isLastStep ? (
                      <Button
                        onClick={handleFinalClose}
                        className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      >
                        {labels.close || "Stäng"}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleNext}
                        className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      >
                        {labels.next || "Nästa"}
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showHowItWorksVideo && (
        <VideoPlayer
          videoUrl={HOW_IT_WORKS_HERO_VIDEO_URL}
          title={howVideoModalTitle}
          onClose={() => setShowHowItWorksVideo(false)}
          defaultPlaybackRate={0.75}
        />
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {labels.confirmTitle ||
                "Är du säker på att du vill stänga guiden?"}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {labels.confirmMessage}
            </p>
            <div className="flex items-center space-x-2 pt-2">
              <input
                type="checkbox"
                id="dontShowAgain"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label
                htmlFor="dontShowAgain"
                className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                {labels.dontShowAgain || "Visa inte igen vid inloggning"}
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleCancelClose}>
                {labels.cancel || "Avbryt"}
              </Button>
              <Button
                onClick={handleConfirmClose}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {labels.ok || "Okej"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
